import * as React from 'react';
import {
  TableHead,
  TableRow,
  TableCell,
  SortableTableHeaderCell,
  makeStyles,
  createStyles,
  Theme,
} from '@bb-ui/react-library';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import { CohortDetail } from 'App.types';
import orderBy from 'lodash/orderBy';
import CohortRow from './CohortRow';
import { PaginatedTable } from 'components/PaginatedTable';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: { width: '100%' },
    row: { height: 56 },
    cell: {
      padding: theme.spacing(1, 2),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  }),
);

export interface CohortsTableProps {
  cohorts: CohortDetail[];
  loading: boolean;
  onViewCohort: (cohortId: string) => void;
}

const CohortsTable: React.FC<CohortsTableProps> = ({ cohorts, loading, onViewCohort }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [sortColumn, setSortColumn] = React.useState<string | undefined>('downtime_date');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc' | undefined>('desc');

  const sortedRows = React.useMemo(() => {
    if (!sortColumn || !sortDirection) return cohorts;
    return orderBy(cohorts, [sortColumn], [sortDirection]);
  }, [cohorts, sortColumn, sortDirection]);

  const handleSortChanged = (params: { sortColumnId?: string; sortDirection?: 'asc' | 'desc' }) => {
    const { sortColumnId, sortDirection: dir } = params;
    setSortColumn(sortColumnId);
    setSortDirection(dir);
    return true;
  };

  const getAriaSortMessage = (columnId?: string, dir?: 'asc' | 'desc') => {
    const labels: Record<string, string> = {
      name: t('cohorts.name'),
      migration_status: t('cohorts.migrationStatus'),
      created_by: t('cohorts.createdBy'),
      downtime_date: t('cohorts.downtimeDate'),
      sites_count: t('cohorts.sitesCount'),
    };
    const direction = dir ?? 'none';
    return `${labels[columnId ?? ''] ?? columnId} sorted ${direction}`;
  };

  if (loading) {
    return <LoadingIndicator data-testid="cohorts-table-loading" />;
  }

  return (
    <PaginatedTable
      sortedData={sortedRows}
      onSortChanged={handleSortChanged}
      getSortChangedAriaMessage={getAriaSortMessage}
      hideToolbar
      renderHead={() => (
        <TableHead>
          <TableRow className={classes.row}>
            <SortableTableHeaderCell
              id="cohorts-table_header_name"
              columnId="name"
              tableCellProps={{ className: classes.cell }}
            >
              {t('cohorts.name')}
            </SortableTableHeaderCell>

            <SortableTableHeaderCell
              id="cohorts-table_header_migration"
              columnId="migration_status"
              tableCellProps={{ className: classes.cell, align: 'center' }}
            >
              {t('cohorts.statusLabel')}
            </SortableTableHeaderCell>

            <SortableTableHeaderCell
              id="cohorts-table_header_creator"
              columnId="created_by"
              tableCellProps={{ className: classes.cell, align: 'center' }}
            >
              {t('cohorts.createdBy')}
            </SortableTableHeaderCell>

            <SortableTableHeaderCell
              id="cohorts-table_header_downtime"
              columnId="downtime_date"
              tableCellProps={{ className: classes.cell, align: 'center' }}
            >
              {t('cohorts.downtimeDate')}
            </SortableTableHeaderCell>

            <SortableTableHeaderCell
              id="cohorts-table_header_sites"
              columnId="sites_count"
              tableCellProps={{ className: classes.cell, align: 'center' }}
            >
              {t('cohorts.sitesCount')}
            </SortableTableHeaderCell>

            <TableCell className={classes.cell} />
          </TableRow>
        </TableHead>
      )}
      renderRow={(cohort, idx) => (
        <CohortRow
          key={cohort.id}
          cohort={cohort}
          index={idx}
          rowClass={classes.row}
          cellTextClass={classes.cell}
          onView={() => onViewCohort(cohort.id)}
        />
      )}
    />
  );
};

export default CohortsTable;
